import type {
  AdvisedProduct,
  CustomerInfoSubmittedEvent,
  Integration,
  InfoPageCustomerInfoSubmittedEvent,
  NoResultsCustomerInfoSubmittedEvent,
} from "../../../../app/types"
import type {
  AdviceEvent,
  AnsweredEvent,
  ConversationStartedEvent,
  Product,
  ProductClickEvent,
  StartOverEvent,
} from "../../types"
import type {
  AdvisorInfo,
  SessionContext,
  UsageAnalyticsEventTransformer,
} from "../types"

/**
 *
 * The standard usage events are the events defined by Aiden itself.
 * These are used by clients for custom integration scripts and therefore,
 * the events can only be changed if they don't break backwards compatibility.
 *
 * */
export const StandardUsageEvents: UsageAnalyticsEventTransformer & {
  startOver: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[]
  ) => StartOverEvent[]
} = {
  questionAnswered: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    question: { title: string; label: string },
    answers: string[]
  ): AnsweredEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integrations: integrations.map((i) => ({ name: i.name })),
        type: "answered-event",
        data: {
          sessionId: context.sessionId,
          question: question.title,
          answers: answers,
        },
      },
    ]
  },

  adviceGiven: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    products: Product[]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  ): AdviceEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integrations: integrations.map((i) => ({ name: i.name })),
        type: "advised-event",
        data: {
          sessionId: context.sessionId,
          products: products.map((p) => ({
            id: p.id,
            clientProductId: p.clientProductId,
            name: p.name,
            image: p.image,
            link: p.link,
          })),
        },
      },
    ]
  },

  startOver: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[]
  ): StartOverEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integrations: integrations.map((i) => ({ name: i.name })),
        type: "start-over-event",
        data: {
          sessionId: context.sessionId,
        },
      },
    ]
  },

  clickedOnProduct: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    product: Product
  ): ProductClickEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integrations: integrations.map((i) => ({ name: i.name })),
        type: "product-clicked-event",
        data: {
          sessionId: context.sessionId,
          productName: product.name,
          productId: product.clientProductId,
          productLink: product.link,
        },
      },
    ]
  },

  customerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      advisedProducts: AdvisedProduct[]
    }
  ): CustomerInfoSubmittedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integrations: integrations.map((i) => ({ name: i.name })),
        type: "customer-info",
        isPreview: isPreview,
        data: {
          email: info.email,
          checkboxChecked: info.checkboxChecked,
          advice: info.advisedProducts.map((product) => {
            const p: Omit<AdvisedProduct, "data"> = {
              id: product.id,
              name: product.name,
              url: product.url,
              imageUrl: product.imageUrl,
              price: product.price,
              salePrice: product.salePrice,
            }
            return p
          }),
        },
      },
    ]
  },

  infoPageCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      formIdentifier: string
    }
  ): InfoPageCustomerInfoSubmittedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integrations: integrations.map((i) => ({ name: i.name })),
        type: "info-page-customer-info",
        isPreview: isPreview,
        data: {
          email: info.email,
          checkboxChecked: info.checkboxChecked,
          formIdentifier: info.formIdentifier,
        },
      },
    ]
  },

  noResultsCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
    }
  ): NoResultsCustomerInfoSubmittedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integrations: integrations.map((i) => ({ name: i.name })),
        type: "no-results-customer-info",
        isPreview: isPreview,
        data: {
          email: info.email,
          checkboxChecked: info.checkboxChecked,
        },
      },
    ]
  },

  conversationStarted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[]
  ): ConversationStartedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integrations: integrations.map((i) => ({ name: i.name })),
        type: "conversation-started-event",
        data: {
          sessionId: context.sessionId,
        },
      },
    ]
  },
}
